import { useMemo } from 'react';
import { useFeatureStore } from '@/hooks/data/feature/useFeatureStore';
import { useShareConfig } from '@/context/ShareConfigContext';
import { useAccount } from '@/hooks/data/account/useAccount';
import { useCurrentPage } from './useCurrentPage';

export interface UserCapabilities {
  canDragAssets: boolean;
  canViewContextMenu: boolean;
  temporaryGuestBlockForHomePage: boolean;
  canViewPrivateMetadata: boolean;
  canViewPrivateAsset: boolean;
  canViewPrivateAlbum: boolean;
  canViewPrivateTabs: boolean;
  canViewComments: boolean;
  canCommentPrivately: boolean;
  canSelectAll: boolean;
  canViewSidePanels: boolean;
  canEditAsset: boolean;
  canDownloadAssets: boolean;
  canViewSettings: boolean;
  canViewTerms: boolean;
  canViewDeleteOrRemoveAction: boolean;
  canUpload: boolean;
  canUndoRedo: boolean;
  canSearch: boolean;
  canViewNotifications: boolean;
  canViewTrash: boolean;
  canManageSubmissionLinks: boolean;
}

// The roles config/rolePermissions.php grants `read:trash`. Kept beside the capability so
// the two are read together when either changes.
const TRASH_READING_ROLES = ['archivist', 'owner'];

// config/rolePermissions.php grants both `read:links` and `create:links` to these same roles.
const SUBMISSION_LINK_ROLES = ['archivist', 'owner'];

const ALL_DENIED: UserCapabilities = {
  canDragAssets: false,
  canViewContextMenu: false,
  temporaryGuestBlockForHomePage: false,
  canViewPrivateMetadata: false,
  canViewPrivateAsset: false,
  canViewPrivateAlbum: false,
  canViewPrivateTabs: false,
  canViewComments: false,
  canCommentPrivately: false,
  canSelectAll: false,
  canViewSidePanels: false,
  canEditAsset: false,
  canDownloadAssets: false,
  canViewSettings: false,
  canViewTerms: false,
  canViewDeleteOrRemoveAction: false,
  canUpload: false,
  canUndoRedo: false,
  canSearch: false,
  canViewNotifications: false,
  canViewTrash: false,
  canManageSubmissionLinks: false,
};

export const useCapabilities = (): UserCapabilities => {
  const { profile, profileLoading, guest, isAnonymous, isAuthenticatedUser } = useAccount();
  const { isPublicSharePage, isTrashPage, isSubmitPage } = useCurrentPage();
  const shareConfig = useShareConfig();
  const shareCollaborationFeature = useFeatureStore((state) => state.share_collaboration);

  return useMemo(() => {
    if (profileLoading || !profile) {
      return ALL_DENIED;
    }

    return {
      // Dragging is disabled in shares, for all users.
      canDragAssets: !isPublicSharePage,
      // Context menu is disabled in shares, for all users.
      canViewContextMenu: !isPublicSharePage,
      // Some features are blocked for guest while we do not have a dedicated landing page for guests
      temporaryGuestBlockForHomePage: guest,
      // Private metadata is hidden in shares.
      canViewPrivateMetadata: !isPublicSharePage,
      // Private asset is hidden in shares.
      canViewPrivateAsset: !isPublicSharePage,
      // Private asset is hidden in shares.
      canViewPrivateAlbum: !isPublicSharePage,
      // Some tabs are hidden in shares.
      canViewPrivateTabs: !isPublicSharePage,
      // Anonymous viewers never see comments. Share viewers must also be members of the
      // exact share, in addition to collaboration being enabled.
      canViewComments: isPublicSharePage
        ? !isAnonymous && Boolean(shareConfig?.collaborationEnabled && shareConfig?.requesterIsMemberOfShare)
        : true,
      // Guests (share collaborators) cannot mark comments as private, and the whole
      // capability is off unless the share_collaboration feature flag is enabled.
      canCommentPrivately: isAuthenticatedUser && shareCollaborationFeature,
      // Select all assets is disabled shares.
      canSelectAll: !isPublicSharePage,
      // The side panel (tree + search) are hidden for guests.
      // Invariant relied on by the sidebar tree's create-folder controls: the only roles that get
      // here are owner and archivist, and both hold `create:folders` in the backend's
      // rolePermissions config, so those controls need no capability gate of their own.
      // Revisit this if a tenant role is added that can see the side panel without that permission.
      canViewSidePanels: !isPublicSharePage && !guest && !isSubmitPage,
      // Assets cannot be edited in shares.
      canEditAsset: !isPublicSharePage,
      // Off a share: the regular DAM zip export, for full authenticated users only.
      // On a share: the share's own (variant-scoped) download — allowed for anyone
      // with an identity (OTP guest or tenant member), denied only for anonymous
      // viewers, matching the backend's `Auth::guard('internal')->user() === null`
      // check in ShareExportCreateRequest/DownloadRequest/ShowRequest.
      canDownloadAssets: isPublicSharePage ? !isAnonymous : isAuthenticatedUser,
      // Guest users cannot access the manage page.
      canViewSettings: !guest,
      // Guest users do not interact with terms and conditions.
      canViewTerms: !guest,
      // Delete or remove are hidden in shares and on the trash page.
      canViewDeleteOrRemoveAction: !isPublicSharePage && !isTrashPage,
      /** Uploads, search and undo/redo are for full authenticated users, never in shares or
       * on the submit page — that's tenant browse chrome, not a contributor's one-page session.
       * canUpload also excludes the trash page: nothing there is a valid upload target. */
      canUpload: isAuthenticatedUser && !isPublicSharePage && !isSubmitPage && !isTrashPage,
      canUndoRedo: isAuthenticatedUser && !isPublicSharePage && !isSubmitPage,
      canSearch: isAuthenticatedUser && !isPublicSharePage && !isSubmitPage,
      /** The notification bell needs an identity; anonymous viewers can't create exports at
       * all (see canDownloadAssets), so they get no bell. Also off on the submit page. */
      canViewNotifications: !isAnonymous && !isSubmitPage,
      // Role, not just `!guest`: administrator is authenticated and not a guest, but
      // `read:trash` in config/rolePermissions.php belongs to archivist and to owner,
      // which is merged from it. `!guest` would let an administrator subscribe and then
      // be refused by the channel - the failed subscription this capability prevents.
      canViewTrash: isAuthenticatedUser && TRASH_READING_ROLES.includes(profile.user?.role),
      // Same role gate as canViewTrash: read:links and create:links both belong to
      // archivist/owner, so an administrator gets a 403 without this check.
      canManageSubmissionLinks: isAuthenticatedUser && SUBMISSION_LINK_ROLES.includes(profile.user?.role),
    };
  }, [
    profile,
    profileLoading,
    guest,
    isAnonymous,
    isAuthenticatedUser,
    isTrashPage,
    isPublicSharePage,
    isSubmitPage,
    shareConfig?.collaborationEnabled,
    shareConfig?.requesterIsMemberOfShare,
    shareCollaborationFeature,
  ]);
};
